import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';

const PrivacyPolicy = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-slate-800">
            Privacy Policy
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="h-[70vh] pr-4">
          <div className="space-y-6 text-slate-700">
            {/* Introduction */}
            <section>
              <p className="text-sm text-slate-600 mb-4">
                <strong>Last Updated:</strong> December 5, 2024
              </p>
              <p className="leading-relaxed">
                Unified Power Private Limited ("we," "us," or "our") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when 
                you visit our website www.unifiedpower.online and use our services.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h3 className="text-lg font-semibold text-slate-800 mb-3">1. Information We Collect</h3>
              
              <h4 className="font-semibold text-slate-800 mb-2">1.1 Personal Information</h4>
              <p className="mb-3 leading-relaxed">
                We may collect personal information that you voluntarily provide to us when you:
              </p>
              <ul className="list-disc pl-6 space-y-1 mb-4">
                <li>Fill out our contact form</li>
                <li>Request information about our services</li>
                <li>Subscribe to our newsletter</li>
                <li>Communicate with us via email or phone</li>
              </ul>
              <p className="mb-4 leading-relaxed">
                This information may include: name, email address, phone number, company name, and any 
                message or inquiry you submit.
              </p>

              <h4 className="font-semibold text-slate-800 mb-2">1.2 Automatically Collected Information</h4>
              <p className="mb-3 leading-relaxed">
                When you visit our website, we may automatically collect certain information about your device, including:
              </p>
              <ul className="list-disc pl-6 space-y-1 mb-4">
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Referring website</li>
                <li>Pages visited and time spent on pages</li>
                <li>Date and time of visits</li>
              </ul>
            </section>

            {/* How We Use Your Information */}
            <section>
              <h3 className="text-lg font-semibold text-slate-800 mb-3">2. How We Use Your Information</h3>
              <p className="mb-3 leading-relaxed">
                We use the information we collect for the following purposes:
              </p>
              <ul className="list-disc pl-6 space-y-1 mb-4">
                <li>To respond to your inquiries and provide customer support</li>
                <li>To process your service requests and project inquiries</li>
                <li>To send you information about our services, updates, and promotional materials (with your consent)</li>
                <li>To improve our website and services</li>
                <li>To analyze website usage and trends</li>
                <li>To prevent fraud and enhance security</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            {/* Disclosure of Your Information */}
            <section>
              <h3 className="text-lg font-semibold text-slate-800 mb-3">3. Disclosure of Your Information</h3>
              <p className="mb-3 leading-relaxed">
                We may share your information in the following circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-1 mb-4">
                <li><strong>Service Providers:</strong> We may share information with third-party service providers who assist us in operating our website and conducting our business</li>
                <li><strong>Business Transfers:</strong> In connection with any merger, sale of company assets, or acquisition</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                <li><strong>With Your Consent:</strong> When you have given us explicit permission to share your information</li>
              </ul>
              <p className="leading-relaxed">
                We do not sell, rent, or trade your personal information to third parties for marketing purposes.
              </p>
            </section>

            {/* Data Security */}
            <section>
              <h3 className="text-lg font-semibold text-slate-800 mb-3">4. Data Security</h3>
              <p className="leading-relaxed">
                We implement appropriate technical and organizational security measures to protect your personal 
                information against unauthorized access, alteration, disclosure, or destruction. However, no method 
                of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee 
                absolute security.
              </p>
            </section>

            {/* Data Retention */}
            <section>
              <h3 className="text-lg font-semibold text-slate-800 mb-3">5. Data Retention</h3>
              <p className="leading-relaxed">
                We retain your personal information only for as long as necessary to fulfill the purposes outlined 
                in this Privacy Policy, unless a longer retention period is required or permitted by law. Contact 
                form submissions are retained for business development purposes and may be deleted upon request.
              </p>
            </section>

            {/* Your Rights */}
            <section>
              <h3 className="text-lg font-semibold text-slate-800 mb-3">6. Your Rights</h3>
              <p className="mb-3 leading-relaxed">
                You have the following rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 space-y-1 mb-4">
                <li><strong>Access:</strong> Request access to your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Objection:</strong> Object to processing of your personal information</li>
                <li><strong>Withdrawal of Consent:</strong> Withdraw consent at any time (where processing is based on consent)</li>
              </ul>
              <p className="leading-relaxed">
                To exercise these rights, please contact us at info@unifiedpower.online
              </p>
            </section>

            {/* Cookies */}
            <section>
              <h3 className="text-lg font-semibold text-slate-800 mb-3">7. Cookies and Tracking Technologies</h3>
              <p className="mb-3 leading-relaxed">
                We use cookies and similar tracking technologies to enhance your browsing experience. Cookies are 
                small text files stored on your device. You can control cookie settings through your browser preferences.
              </p>
              <p className="leading-relaxed">
                Types of cookies we use:
              </p>
              <ul className="list-disc pl-6 space-y-1 mb-4">
                <li><strong>Essential Cookies:</strong> Required for website functionality</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our website</li>
                <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
              </ul>
            </section>

            {/* Third-Party Links */}
            <section>
              <h3 className="text-lg font-semibold text-slate-800 mb-3">8. Third-Party Links</h3>
              <p className="leading-relaxed">
                Our website may contain links to third-party websites. We are not responsible for the privacy 
                practices or content of these external sites. We encourage you to review the privacy policies 
                of any third-party sites you visit.
              </p>
            </section>

            {/* Children's Privacy */}
            <section>
              <h3 className="text-lg font-semibold text-slate-800 mb-3">9. Children's Privacy</h3>
              <p className="leading-relaxed">
                Our services are not directed to individuals under the age of 18. We do not knowingly collect 
                personal information from children. If we become aware that we have collected personal information 
                from a child without parental consent, we will take steps to delete that information.
              </p>
            </section>

            {/* Changes to Privacy Policy */}
            <section>
              <h3 className="text-lg font-semibold text-slate-800 mb-3">10. Changes to This Privacy Policy</h3>
              <p className="leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting 
                the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review 
                this Privacy Policy periodically for any changes.
              </p>
            </section>

            {/* Contact Information */}
            <section>
              <h3 className="text-lg font-semibold text-slate-800 mb-3">11. Contact Us</h3>
              <p className="mb-3 leading-relaxed">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, 
                please contact us:
              </p>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <p className="font-semibold text-slate-800 mb-2">Unified Power Private Limited</p>
                <p className="text-sm">Email: info@unifiedpower.online</p>
                <p className="text-sm">Phone: +91 9540504248</p>
                <p className="text-sm">Website: www.unifiedpower.online</p>
              </div>
            </section>

            {/* Consent */}
            <section>
              <h3 className="text-lg font-semibold text-slate-800 mb-3">12. Consent</h3>
              <p className="leading-relaxed">
                By using our website and services, you consent to the collection and use of your information as 
                described in this Privacy Policy. If you do not agree with this policy, please do not use our website.
              </p>
            </section>

            {/* Governing Law */}
            <section className="pb-4">
              <h3 className="text-lg font-semibold text-slate-800 mb-3">13. Governing Law</h3>
              <p className="leading-relaxed">
                This Privacy Policy is governed by and construed in accordance with the laws of India. Any disputes 
                arising from this Privacy Policy shall be subject to the exclusive jurisdiction of the courts in India.
              </p>
            </section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default PrivacyPolicy;
